/**
 * One visit, in the Android app's order (ADR-0026):
 *
 *   opened           「商品のボタンを押してください」 + the machine's countdown
 *   selected         the product and price the MACHINE reported, the store's
 *                    payment methods, 「支払い ¥N」 / 「商品の選択解除」
 *   payment_pending  お支払い中… (the provider's page)
 *   paid / driving   支払成功 → 商品搬出中です
 *   completed        商品が正常に購入されました
 *   failed/refunded  refunded automatically, as on Android
 *   refund_due       the drive timed out: 返金する / 操作を続ける
 *
 * Everything shown comes from the server's snapshot, and the buttons are
 * exactly its `allowed_actions`. This page never sends an amount.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ActionKeys,
  NetworkError,
  cancelVisit,
  errorKey,
  getChannels,
  payVisit,
  redriveVisit,
  refundVisit,
} from "../api";
import { ChannelPicker } from "../ChannelPicker";
import { AppBar, ErrorCard, Spinner, StatusIcon, Steps } from "../components";
import { useI18n } from "../i18n";
import { handoffMark } from "../paymentReturn";
import { store } from "../storage";
import { reportError, reportVisit } from "../support/supportContext";
import { useCountdown } from "../useCountdown";
import { useVisit } from "../useVisit";
import {
  TERMINAL,
  formatYen,
  isResumable,
  outcomeKey,
  presentation,
  type Channel,
  type ChannelOption,
  type VendSession,
} from "../vend";

type Busy = null | "pay" | "cancel" | "refund" | "redrive";

export function VisitRoute() {
  const { id = "" } = useParams();
  // A new id is a new visit: start from a clean screen, not the old one's state.
  return <VisitScreen key={id} id={id} />;
}

function VisitScreen({ id }: { id: string }) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const flash = (location.state as { flash?: string } | null)?.flash ?? null;
  const { visit, error, link, replace } = useVisit(id);
  const keys = useRef(new ActionKeys());
  const [busy, setBusy] = useState<Busy>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  // Back from the payment page (bfcache restores this page as it was left).
  useEffect(() => {
    const reset = () => setRedirecting(false);
    window.addEventListener("pageshow", reset);
    return () => window.removeEventListener("pageshow", reset);
  }, []);

  // The help chat tells the engineers which visit the customer is looking at.
  useEffect(() => {
    if (visit) reportVisit(visit);
  }, [visit]);

  useEffect(() => {
    if (error) reportError(errorKey(error), error);
  }, [error]);

  // Forget the visit once nothing more can happen to it.
  useEffect(() => {
    if (visit && !isResumable(visit) && store.get("visit") === visit.vend_session_id) {
      store.remove("visit");
      store.remove("qr");
    }
  }, [visit]);

  async function act(name: Exclude<Busy, null>, call: (key: string) => Promise<VendSession>) {
    setBusy(name);
    setActionError(null);
    try {
      const v = await call(keys.current.for(name));
      keys.current.done(name);
      replace(v);
      return v;
    } catch (e) {
      // A dropped connection keeps the key: retrying the same action is safe.
      if (!(e instanceof NetworkError)) keys.current.done(name);
      reportError(errorKey(e), e);
      setActionError(t(errorKey(e)));
      return null;
    } finally {
      setBusy(null);
    }
  }

  async function pay(channel: Channel) {
    const v = await act("pay", (key) => payVisit(id, channel, key));
    const target = v?.payment?.redirect_url ?? v?.payment?.deeplink;
    if (target) {
      setRedirecting(true);
      // Same tab: the visit is remembered and resumes when the customer returns.
      store.set("paying", handoffMark(id));
      window.location.assign(target);
    }
  }

  function goHome() {
    if (visit && !isResumable(visit)) {
      store.remove("visit");
      store.remove("qr");
    }
    navigate("/");
  }

  if (!visit) {
    return (
      <div className="aseed-shell">
        <AppBar onHome={goHome} />
        <main className="aseed-shell__main">
          <div className="aseed-shell__content v-stack">
            {error ? (
              <>
                <ErrorCard>{t(errorKey(error))}</ErrorCard>
                <button
                  type="button"
                  className="aseed-button v-wide"
                  onClick={() => {
                    store.remove("visit");
                    store.remove("qr");
                    navigate("/");
                  }}
                >
                  {t("vend.home")}
                </button>
              </>
            ) : (
              <div className="v-center v-stack">
                <Spinner label={t("common.loading")} />
                <p className="v-muted">{t("vend.connecting")}</p>
              </div>
            )}
          </div>
        </main>
      </div>
    );
  }

  const { tone, step } = presentation(visit.state);
  const disabled = busy !== null || redirecting;
  const showSteps = !["canceled", "expired", "refunded"].includes(visit.state);

  return (
    <div className="aseed-shell">
      <AppBar onHome={goHome} />
      <main className="aseed-shell__main">
        <div className="aseed-shell__content v-stack">
          {showSteps ? <Steps current={step} problem={tone === "problem"} /> : null}
          {flash ? <div className="aseed-status-banner v-note">{t(flash)}</div> : null}
          {actionError ? <ErrorCard>{actionError}</ErrorCard> : null}
          {link === "reconnecting" ? (
            <p className="v-muted v-small v-center">{t("vend.live.reconnecting")}</p>
          ) : null}

          {visit.state === "opened" ? (
            <SelectOnMachine visit={visit} />
          ) : visit.state === "selected" ? (
            <ChooseMethod
              visit={visit}
              locale={locale}
              disabled={disabled}
              busy={busy}
              onPay={(c) => void pay(c)}
              onDeselect={() => void act("cancel", (key) => cancelVisit(id, key))}
            />
          ) : (
            <Outcome visit={visit} locale={locale} />
          )}
        </div>
      </main>

      {visit.state === "opened" ? (
        <div className="aseed-sticky-actions v-actions">
          <button
            type="button"
            className="aseed-button aseed-button--cancel v-wide"
            disabled={disabled}
            onClick={() => void act("cancel", (key) => cancelVisit(id, key))}
          >
            {t("vend.prompt.cancel")}
          </button>
        </div>
      ) : visit.state === "selected" ? null : (
        <OutcomeActions
          visit={visit}
          disabled={disabled}
          busy={busy}
          onRefund={() => void act("refund", (key) => refundVisit(id, key))}
          onRedrive={() => void act("redrive", (key) => redriveVisit(id, key))}
          onAgain={goHome}
        />
      )}

      {redirecting ? (
        <div className="v-busy" role="status" aria-live="polite">
          <Spinner />
          <p>{t("vend.redirecting")}</p>
        </div>
      ) : null}
    </div>
  );
}

// ------------------------------------------------- 1. select on the machine

function SelectOnMachine({ visit }: { visit: VendSession }) {
  const { t } = useI18n();
  const left = useCountdown(visit.seconds_left, visit.observed_at);
  return (
    <section className="v-card v-prompt" aria-live="polite">
      <h1 className="v-prompt__title">{t("vend.prompt.title")}</h1>
      <div className="v-prompt__body">
        <MachinePicture />
        {left !== null ? <CountdownRing left={left} total={visit.seconds_left ?? left} /> : null}
      </div>
      <p className="v-prompt__hint">{t("vend.prompt.hint")}</p>
      <p className="v-prompt__tip">{t("vend.prompt.tip")}</p>
    </section>
  );
}

/** A vending machine with one button calling for a press. Original drawing. */
function MachinePicture() {
  return (
    <svg className="v-machine" viewBox="0 0 120 170" aria-hidden="true">
      <rect x="6" y="4" width="108" height="162" rx="10" className="v-machine__body" />
      <rect x="16" y="14" width="62" height="96" rx="4" className="v-machine__window" />
      {[0, 1, 2].map((row) =>
        [0, 1, 2].map((col) => (
          <rect
            key={`${row}-${col}`}
            x={22 + col * 19}
            y={22 + row * 30}
            width="12"
            height="22"
            rx="3"
            className="v-machine__can"
          />
        )),
      )}
      {[0, 1, 2].map((row) =>
        [0, 1, 2].map((col) => (
          <rect
            key={`b-${row}-${col}`}
            x={22 + col * 19}
            y={46 + row * 30}
            width="12"
            height="4"
            rx="2"
            className={row === 1 && col === 1 ? "v-machine__button v-machine__button--press" : "v-machine__button"}
          />
        )),
      )}
      <rect x="86" y="18" width="20" height="30" rx="3" className="v-machine__panel" />
      <rect x="86" y="56" width="20" height="10" rx="2" className="v-machine__return" />
      <rect x="22" y="126" width="76" height="24" rx="4" className="v-machine__outlet" />
    </svg>
  );
}

function CountdownRing({ left, total }: { left: number; total: number }) {
  const { t } = useI18n();
  const r = 34;
  const c = 2 * Math.PI * r;
  const fraction = total > 0 ? Math.max(0, Math.min(1, left / total)) : 0;
  return (
    <div className={`v-ring${left <= 5 ? " v-ring--urgent" : ""}`} role="timer" aria-live="off">
      <svg viewBox="0 0 80 80" aria-hidden="true">
        <circle cx="40" cy="40" r={r} className="v-ring__track" />
        <circle
          cx="40"
          cy="40"
          r={r}
          className="v-ring__bar"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - fraction)}
        />
      </svg>
      <span className="v-ring__value">{left}</span>
      <span className="aseed-visually-hidden">{t("vend.prompt.seconds", { seconds: left })}</span>
    </div>
  );
}

// ------------------------------------------------------- 2. choose a method

function ChooseMethod({
  visit,
  locale,
  disabled,
  busy,
  onPay,
  onDeselect,
}: {
  visit: VendSession;
  locale: string;
  disabled: boolean;
  busy: Busy;
  onPay: (c: Channel) => void;
  onDeselect: () => void;
}) {
  const { t } = useI18n();
  const left = useCountdown(visit.seconds_left, visit.observed_at);
  const [options, setOptions] = useState<ChannelOption[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [chosen, setChosen] = useState<Channel | null>(null);
  const [remember, setRemember] = useState<boolean>(() => store.get("channel") !== null);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoadError(null);
    getChannels(visit.vend_session_id, ctrl.signal)
      .then((list) => {
        setOptions(list);
        // The remembered method if the store offers it, else the store's first.
        const usable = list.filter((o) => o.available);
        const saved = store.get("channel");
        setChosen(
          (saved && usable.some((o) => o.channel === saved) ? saved : null) ??
            usable[0]?.channel ??
            null,
        );
      })
      .catch((e) => {
        if (ctrl.signal.aborted) return;
        reportError(errorKey(e), e);
        setLoadError(t(errorKey(e)));
      });
    return () => ctrl.abort();
    // The list is read once per visit (and per retry), not per snapshot.
  }, [visit.vend_session_id, attempt, t]);

  const amount = typeof visit.amount_yen === "number" ? formatYen(visit.amount_yen, locale) : null;
  const productLabel = visit.product_name ?? (typeof visit.grid_no === "number"
    ? t("vend.slot", { slot: visit.grid_no })
    : null);
  const usable = useMemo(() => (options ?? []).filter((o) => o.available), [options]);

  function payNow() {
    if (!chosen) return;
    if (remember) store.set("channel", chosen);
    else store.remove("channel");
    onPay(chosen);
  }

  return (
    <>
      <section className="v-card v-product" aria-live="polite">
        {productLabel ? <p className="v-product__name">{productLabel}</p> : null}
        <div className="aseed-amount aseed-amount--total">
          <span className="aseed-amount__label">{t("vend.product.price")}</span>
          <span className="aseed-amount__value">{amount ?? "—"}</span>
        </div>
        {left !== null ? (
          <p className={`v-occupancy${left <= 10 ? " v-occupancy--urgent" : ""}`} role="timer">
            {t("vend.method.occupancy", { seconds: left })}
          </p>
        ) : null}
      </section>

      {visit.outcome_reason === "payment_failed" ? (
        <div className="aseed-status-banner aseed-status-banner--error" role="alert">
          <strong>{t("vend.payment.failed.title")}</strong>
          <br />
          {t("vend.payment.failed.hint")}
        </div>
      ) : null}

      <section className="v-card">
        <h2 className="v-section-title">{t("vend.method.title")}</h2>
        {loadError ? (
          <div className="v-stack">
            <ErrorCard>{loadError}</ErrorCard>
            <button type="button" className="aseed-button" onClick={() => setAttempt((n) => n + 1)}>
              {t("common.retry")}
            </button>
          </div>
        ) : options === null ? (
          <div className="v-center">
            <Spinner label={t("vend.method.loading")} />
          </div>
        ) : usable.length === 0 ? (
          <p className="v-muted">{t("vend.method.none")}</p>
        ) : (
          <>
            <ChannelPicker options={options} value={chosen} onChange={setChosen} disabled={disabled} />
            <label className="v-check">
              <input
                type="checkbox"
                name="remember-method"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              <span>{t("vend.method.remember")}</span>
            </label>
          </>
        )}
      </section>

      <div className="aseed-sticky-actions v-actions">
        <button
          type="button"
          className="aseed-button v-wide"
          disabled={disabled || !chosen || amount === null}
          onClick={payNow}
        >
          {busy === "pay" ? <Spinner /> : null}
          {amount ? t("vend.method.pay", { amount }) : t("vend.state.opened.title")}
        </button>
        <button type="button" className="v-link v-wide" disabled={disabled} onClick={onDeselect}>
          {t("vend.method.deselect")}
        </button>
      </div>
    </>
  );
}

// ------------------------------------------------------- 3. the outcome

function Outcome({ visit, locale }: { visit: VendSession; locale: string }) {
  const { t } = useI18n();
  const { tone } = presentation(visit.state);
  const amount = typeof visit.amount_yen === "number" ? formatYen(visit.amount_yen, locale) : null;
  const explained = outcomeKey(visit);
  const showReference = tone === "problem" || visit.state === "refunded";
  const productLabel = visit.product_name ?? (typeof visit.grid_no === "number"
    ? t("vend.slot", { slot: visit.grid_no })
    : null);

  return (
    <>
      <section className={`v-status v-status--${tone}`} aria-live="polite">
        <StatusIcon tone={tone} />
        <h1 className="v-status__title">{t(`vend.state.${visit.state}.title`)}</h1>
        <p className="v-status__hint">{t(explained ?? `vend.state.${visit.state}.hint`)}</p>
      </section>

      {visit.unfulfilled && visit.state !== "unknown" && visit.state !== "refund_due" ? (
        <div className="aseed-status-banner aseed-status-banner--unfulfilled">{t("operation.unfulfilled")}</div>
      ) : null}

      {amount || productLabel ? (
        <section className="v-card v-summary">
          <div className="v-summary__row">
            <span className="v-muted">{t("vend.machine", { id: "" }).trim()}</span>
            <span className="v-summary__value">{visit.machine_id}</span>
          </div>
          {productLabel ? (
            <div className="v-summary__row">
              <span className="v-muted">
                {visit.product_name ? t("vend.steps.select") : t("vend.slot", { slot: "" }).trim()}
              </span>
              <span className="v-summary__value">{productLabel}</span>
            </div>
          ) : null}
          {visit.channel ? (
            <div className="v-summary__row">
              <span className="v-muted">{t("vend.method")}</span>
              <span className="v-summary__value">{t(`payment.channel.${visit.channel}`)}</span>
            </div>
          ) : null}
          {amount ? (
            <div className="aseed-amount aseed-amount--total v-summary__total">
              <span className="aseed-amount__label">
                {visit.state === "refund_due" ? t("vend.paid.amount") : t("vend.price")}
              </span>
              <span className="aseed-amount__value">{amount}</span>
            </div>
          ) : null}
          {showReference ? (
            <div className="v-summary__row v-reference">
              <span className="v-muted">{t("vend.reference")}</span>
              <span className="v-summary__value v-mono">{visit.vend_session_id}</span>
            </div>
          ) : null}
        </section>
      ) : null}
    </>
  );
}

function OutcomeActions({
  visit,
  disabled,
  busy,
  onRefund,
  onRedrive,
  onAgain,
}: {
  visit: VendSession;
  disabled: boolean;
  busy: Busy;
  onRefund: () => void;
  onRedrive: () => void;
  onAgain: () => void;
}) {
  const { t } = useI18n();
  const can = new Set(visit.allowed_actions);
  const finished = TERMINAL.has(visit.state) && !can.has("request_refund") && !can.has("redrive");
  return (
    <div className="aseed-sticky-actions v-actions">
      {can.has("open_payment") && visit.payment?.redirect_url ? (
        <a
          className="aseed-button v-wide v-button-link"
          href={visit.payment.redirect_url}
          onClick={() => store.set("paying", handoffMark(visit.vend_session_id))}
        >
          {t("vend.openPayment")}
        </a>
      ) : null}
      {can.has("open_payment") && visit.payment?.deeplink ? (
        <a
          className="aseed-button v-wide v-button-link v-button-ghost"
          href={visit.payment.deeplink}
          onClick={() => store.set("paying", handoffMark(visit.vend_session_id))}
        >
          {t("vend.openApp")}
        </a>
      ) : null}
      {can.has("redrive") ? (
        <button type="button" className="aseed-button v-wide" disabled={disabled} onClick={onRedrive}>
          {busy === "redrive" ? <Spinner /> : null}
          {t("vend.redrive")}
        </button>
      ) : null}
      {can.has("request_refund") ? (
        <button
          type="button"
          className="aseed-button aseed-button--danger v-wide"
          disabled={disabled}
          onClick={onRefund}
        >
          {busy === "refund" ? <Spinner /> : null}
          {t("vend.refund.now")}
        </button>
      ) : null}
      {finished ? (
        <button type="button" className="aseed-button v-wide" onClick={onAgain}>
          {t("vend.again")}
        </button>
      ) : null}
    </div>
  );
}
